/**
 * Sidebar Component - Gemini-Inspired Design
 * Beautiful collapsible sidebar with smooth Framer Motion animations
 */
import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth, useUser, useClerk, UserButton } from '@clerk/clerk-react';
import { motion, AnimatePresence } from 'framer-motion';
import { getChats, createChat, deleteChat } from '../../services/api';
import { waitForClerkToken } from '../../services/clerkToken';
import { isAdmin } from '../../utils/roleCheck';
import { useLanguage } from '../../contexts/LanguageContext';
import './SidebarGemini.css';

// Gemini-style sparkle icon
const GeminiIcon = () => (
  <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
    <defs>
      <linearGradient id="gemini-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#4285f4" />
        <stop offset="50%" stopColor="#9b72cb" />
        <stop offset="100%" stopColor="#d96570" />
      </linearGradient>
    </defs>
    <path
      d="M14 2L16.5 9.5L24 12L16.5 14.5L14 22L11.5 14.5L4 12L11.5 9.5L14 2Z"
      fill="url(#gemini-gradient)"
    />
  </svg>
);

const clerkUserButtonDarkAppearance = {
  variables: {
    colorPrimary: '#1a73e8',
    colorBackground: '#ffffff',
    colorInputBackground: '#f8fafd',
    colorInputText: '#1f1f1f',
    colorText: '#1f1f1f',
    colorTextSecondary: '#5f6368',
    colorTextOnPrimaryBackground: '#ffffff',
  },
  elements: {
    userButtonAvatarBox: {
      width: '40px',
      height: '40px'
    },
    userButtonPopoverCard: {
      pointerEvents: 'auto',
      transform: 'translateY(-8px)',
      background: '#ffffff',
      border: '1px solid #dfe5ee',
      boxShadow: '0 22px 56px rgba(60, 64, 67, 0.18)'
    },
    userButtonPopoverMain: {
      background: 'transparent'
    },
    userButtonPopoverActions: {
      background: 'transparent'
    },
    userButtonPopoverActionButton: {
      color: '#1f1f1f',
      borderRadius: '0.9rem',
      background: 'transparent'
    },
    userButtonPopoverActionButtonText: {
      color: '#1f1f1f'
    },
    userButtonPopoverActionButtonIcon: {
      color: '#5f6368'
    },
    userButtonPopoverFooter: {
      borderTop: '1px solid #dfe5ee',
      background: 'transparent'
    },
    userPreviewMainIdentifier: {
      color: '#1f1f1f'
    },
    userPreviewSecondaryIdentifier: {
      color: '#5f6368'
    },
    modalBackdrop: {
      background: 'rgba(32, 33, 36, 0.28)'
    },
    modalContent: {
      background: '#ffffff'
    },
    card: {
      background: '#ffffff',
      border: '1px solid #dfe5ee',
      boxShadow: '0 28px 80px rgba(60, 64, 67, 0.18)',
      borderRadius: '24px'
    },
    navbar: {
      background: '#f8fafd',
      borderRightColor: '#dfe5ee'
    },
    navbarButton: {
      color: '#1f1f1f'
    },
    navbarButtonActive: {
      color: '#1a73e8',
      fontWeight: '700',
      background: '#e8f0fe'
    },
    pageScrollBox: {
      background: '#ffffff'
    },
    page: {
      background: '#ffffff'
    },
    profileSectionTitle: {
      color: '#1f1f1f'
    },
    profileSectionTitleText: {
      color: '#1f1f1f'
    },
    profileSectionContent: {
      color: '#1f1f1f'
    },
    profileSectionPrimaryButton: {
      color: '#1f1f1f',
      borderColor: '#dfe5ee',
      background: '#ffffff'
    },
    formFieldLabel: {
      color: '#1f1f1f'
    },
    formFieldInput: {
      background: '#f8fafd',
      color: '#1f1f1f',
      border: '1px solid #dfe5ee'
    },
    menuItem: {
      color: '#1f1f1f'
    },
    menuItemButton: {
      color: '#1f1f1f'
    },
    headerTitle: {
      color: '#1f1f1f'
    },
    headerSubtitle: {
      color: '#5f6368'
    },
    breadcrumbs: {
      color: '#5f6368'
    },
    breadcrumbsItem: {
      color: '#5f6368'
    },
    breadcrumbsItemDivider: {
      color: '#5f6368'
    }
  }
};

const Sidebar = ({ theme, toggleTheme }) => {
  const { t, languageName, changeLanguage } = useLanguage();
  const { user: clerkUser } = useUser();
  const pinnedStorageKey = `tuliza:pinnedChats:${clerkUser?.id || 'guest'}`;
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const [chats, setChats] = useState([]);
  const [activeChat, setActiveChat] = useState(null);
  const [hoveredChat, setHoveredChat] = useState(null);
  const [showLanguageSubmenu, setShowLanguageSubmenu] = useState(false);
  const [typingTitles, setTypingTitles] = useState({});
  const [openDropdown, setOpenDropdown] = useState(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 280 });
  const [pinnedChats, setPinnedChats] = useState(new Set());
  const [renamingChat, setRenamingChat] = useState(null);
  const [newChatTitle, setNewChatTitle] = useState('');
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [activeSubmenu, setActiveSubmenu] = useState(null);
  const [sidebarNotice, setSidebarNotice] = useState('');
  const typingIntervalsRef = useRef({});
  const chatTitlesRef = useRef({});
  const noticeTimeoutRef = useRef(null);

  const { user, isLoaded } = useUser();
  const { isLoaded: isAuthLoaded, isSignedIn } = useAuth();
  const { signOut } = useClerk();

  useEffect(() => {
    if (!isLoaded) {
      return;
    }

    if (!user) {
      setChats([]);
      setActiveChat(null);
      return;
    }

    if (!isAuthLoaded || !isSignedIn) {
      return;
    }

    let cancelled = false;

    const bootstrapSidebarChats = async () => {
      const token = await waitForClerkToken({ retries: 10, delayMs: 250 });

      if (!token || cancelled) {
        return;
      }

      await loadChats();
    };

    bootstrapSidebarChats();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!user) {
      setChats([]);
      setActiveChat(null);
      return;
    }

    if (!isAuthLoaded || !isSignedIn) {
      return;
    }

    let cancelled = false;

    const syncSidebarChats = async () => {
      const token = await waitForClerkToken({ retries: 10, delayMs: 250 });

      if (!token || cancelled) {
        return;
      }

      await loadChats();
    };

    syncSidebarChats();

    return () => {
      cancelled = true;
    };
  }, [user, isAuthLoaded, isSignedIn]);

  useEffect(() => {
    try {
      const storedPins = localStorage.getItem(pinnedStorageKey);
      if (!storedPins) {
        setPinnedChats(new Set());
        return;
      }

      const parsedPins = JSON.parse(storedPins);
      setPinnedChats(new Set(Array.isArray(parsedPins) ? parsedPins : []));
    } catch (error) {
      console.error('Failed to restore pinned chats:', error);
      setPinnedChats(new Set());
    }
  }, [pinnedStorageKey]);

  useEffect(() => {
    localStorage.setItem(pinnedStorageKey, JSON.stringify(Array.from(pinnedChats)));
  }, [pinnedChats, pinnedStorageKey]);

  useEffect(() => {
    const handleChatTitleUpdate = () => {
      if (user && isAuthLoaded && isSignedIn) {
        loadChats();
      }
    };
    window.addEventListener('chat-title-updated', handleChatTitleUpdate);
    return () => {
      window.removeEventListener('chat-title-updated', handleChatTitleUpdate);
    };
  }, [user, isAuthLoaded, isSignedIn]);

  useEffect(() => {
    const handleToggleSidebar = () => {
      if (window.innerWidth > 860) {
        setIsCollapsed(prev => !prev);
      } else {
        setIsMobileOpen(prev => !prev);
      }
    };
    window.addEventListener('toggle-sidebar', handleToggleSidebar);
    return () => window.removeEventListener('toggle-sidebar', handleToggleSidebar);
  }, []);

  // Sync sidebar width CSS variable so main-content can add margin-left.
  // Hover-expand is an overlay, so the reserved width does NOT change on hover.
  useEffect(() => {
    const updateSidebarVar = () => {
      const isDesktop = window.innerWidth > 860;
      document.documentElement.style.setProperty(
        '--sidebar-w',
        isDesktop ? (isCollapsed ? '46px' : '260px') : '0px'
      );
    };
    updateSidebarVar();
    window.addEventListener('resize', updateSidebarVar);
    return () => {
      window.removeEventListener('resize', updateSidebarVar);
      document.documentElement.style.setProperty('--sidebar-w', '0px');
    };
  }, [isCollapsed]);

  const loadChats = async () => {
    try {
      const data = await getChats();
      const chatsList = Array.isArray(data) ? data : (data.results || []);

      chatsList.forEach(chat => {
        const previousTitle = chatTitlesRef.current[chat.url_code];
        const currentTitle = chat.title;

        if (previousTitle === 'New Chat' && currentTitle !== 'New Chat') {
          startTitleTyping(chat.url_code, currentTitle);
        }
        chatTitlesRef.current[chat.url_code] = currentTitle;
      });

      setChats(chatsList);
      if (chatsList.length > 0 && !activeChat) {
        setActiveChat(chatsList[0].url_code);
      }
    } catch (error) {
      console.error('Failed to load chats:', error);
      setChats([]);
    }
  };

  const toggleSidebar = () => setIsCollapsed(!isCollapsed);
  const toggleMobileSidebar = () => setIsMobileOpen(!isMobileOpen);
  const closeMobileSidebar = () => setIsMobileOpen(false);

  const handleToggleBtnClick = () => {
    if (window.innerWidth > 860) {
      toggleSidebar();
    } else {
      closeMobileSidebar();
    }
  };

  const showNotice = (message) => {
    setSidebarNotice(message);

    if (noticeTimeoutRef.current) {
      clearTimeout(noticeTimeoutRef.current);
    }

    noticeTimeoutRef.current = setTimeout(() => {
      setSidebarNotice('');
    }, 2200);
  };

  const handleNewChat = async () => {
    try {
      if (user) {
        const token = await waitForClerkToken({ retries: 8, delayMs: 250 });
        if (!token) {
          showNotice('Still connecting your account. Try again in a moment.');
          return;
        }
      }

      const newChat = await createChat();
      const currentChats = Array.isArray(chats) ? chats : [];
      setChats([newChat, ...currentChats]);
      setActiveChat(newChat.url_code);
      chatTitlesRef.current[newChat.url_code] = newChat.title;
      navigate(`/chat?chat=${newChat.url_code}`);
      closeMobileSidebar();
    } catch (error) {
      console.error('Failed to create chat:', error);
      showNotice('Could not create chat yet. Retrying auth usually fixes this.');
    }
  };

  const handleOpenVoice = () => {
    window.dispatchEvent(new Event('open-voice-conversation'));
    closeMobileSidebar();
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.chat-dropdown-container')) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleShareChat = (chatUrlCode) => {
    const chatUrl = `${window.location.origin}/?chat=${chatUrlCode}`;
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(chatUrl).then(() => {
        showNotice('Share link copied');
      }).catch(() => {
        prompt('Copy this link:', chatUrl);
        showNotice('Copy the link from the prompt');
      });
    } else {
      prompt('Copy this link:', chatUrl);
      showNotice('Copy the link from the prompt');
    }
    setOpenDropdown(null);
  };

  const handlePinChat = (chatUrlCode) => {
    setPinnedChats(prev => {
      const newSet = new Set(prev);
      if (newSet.has(chatUrlCode)) {
        newSet.delete(chatUrlCode);
      } else {
        newSet.add(chatUrlCode);
      }
      return newSet;
    });
    setOpenDropdown(null);
  };

  const handleRenameChat = (chatUrlCode, currentTitle) => {
    setRenamingChat(chatUrlCode);
    setNewChatTitle(currentTitle);
    setOpenDropdown(null);
  };

  const handleSaveRename = async (e, chatUrlCode) => {
    e.preventDefault();
    if (newChatTitle.trim()) {
      try {
        // Update chat title in state
        setChats(prev => prev.map(chat => 
          chat.url_code === chatUrlCode 
            ? { ...chat, title: newChatTitle.trim() }
            : chat
        ));
        
        // Trigger chat title update event
        window.dispatchEvent(new Event('chat-title-updated'));
      } catch (error) {
        console.error('Failed to rename chat:', error);
      }
    }
    setRenamingChat(null);
    setNewChatTitle('');
  };

  const handleCancelRename = () => {
    setRenamingChat(null);
    setNewChatTitle('');
  };

  const handleDeleteChat = async (e, chatUrlCode) => {
    e.preventDefault();
    e.stopPropagation();

    if (!window.confirm('Are you sure you want to delete this chat?')) return;

    try {
      await deleteChat(chatUrlCode);
      const updatedChats = chats.filter(c => c.url_code !== chatUrlCode);
      setChats(updatedChats);
      setPinnedChats(prev => {
        const nextPins = new Set(prev);
        nextPins.delete(chatUrlCode);
        return nextPins;
      });
      showNotice('Chat deleted');

      if (activeChat === chatUrlCode) {
        if (updatedChats.length > 0) {
          setActiveChat(updatedChats[0].url_code);
          navigate(`/chat?chat=${updatedChats[0].url_code}`);
        } else {
          setActiveChat(null);
          navigate('/');
        }
      }
    } catch (error) {
      console.error('Failed to delete chat:', error);
    }
    setOpenDropdown(null);
  };

  const startTitleTyping = (chatId, fullTitle) => {
    if (typingIntervalsRef.current[chatId]) {
      clearInterval(typingIntervalsRef.current[chatId]);
    }

    let currentIndex = 0;
    setTypingTitles(prev => ({ ...prev, [chatId]: '' }));

    typingIntervalsRef.current[chatId] = setInterval(() => {
      if (currentIndex <= fullTitle.length) {
        setTypingTitles(prev => ({
          ...prev,
          [chatId]: fullTitle.substring(0, currentIndex)
        }));
        currentIndex++;
      } else {
        clearInterval(typingIntervalsRef.current[chatId]);
        delete typingIntervalsRef.current[chatId];
        setTypingTitles(prev => {
          const updated = { ...prev };
          delete updated[chatId];
          return updated;
        });
      }
    }, 30);
  };

  const formatDate = (chat) => {
    const dateString = chat.last_message?.created_at || chat.updated_at;
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days === 1) return 'Yesterday';
    if (days < 7) return `${days}d ago`;
    return date.toLocaleDateString();
  };

  const orderedChats = [...chats].sort((left, right) => {
    const leftPinned = pinnedChats.has(left.url_code);
    const rightPinned = pinnedChats.has(right.url_code);

    if (leftPinned !== rightPinned) {
      return leftPinned ? -1 : 1;
    }

    return new Date(right.updated_at || right.created_at || 0) - new Date(left.updated_at || left.created_at || 0);
  });

  const getUserInitials = () => {
    if (!user) return 'U';
    if (user.firstName && user.lastName) {
      return (user.firstName[0] + user.lastName[0]).toUpperCase();
    }
    if (user.firstName) return user.firstName.substring(0, 2).toUpperCase();
    if (user.emailAddresses && user.emailAddresses[0]) {
      return user.emailAddresses[0].emailAddress.substring(0, 2).toUpperCase();
    }
    return 'U';
  };

  const getUserName = () => {
    if (!user) return 'User';
    if (user.firstName && user.lastName) return `${user.firstName} ${user.lastName}`;
    if (user.firstName) return user.firstName;
    if (user.username) return user.username;
    return 'User';
  };

  const getUserEmail = () => {
    if (!user || !user.emailAddresses || user.emailAddresses.length === 0) return '';
    return user.emailAddresses[0].emailAddress;
  };

  const languages = [
    { code: 'en', name: 'English' },
    { code: 'es', name: 'Espanol' },
    { code: 'fr', name: 'Francais' },
    { code: 'de', name: 'Deutsch' },
    { code: 'zh', name: 'Chinese' },
    { code: 'ar', name: 'Arabic' },
    { code: 'hi', name: 'Hindi' },
    { code: 'pt', name: 'Portugues' },
    { code: 'sw', name: 'Swahili' },
    { code: 'so', name: 'Somali' },
    { code: 'yo', name: 'Yoruba' },
    { code: 'ig', name: 'Igbo' },
    { code: 'am', name: 'Amharic' },
    { code: 'ha', name: 'Hausa' },
    { code: 'zu', name: 'Zulu' },
    { code: 'xh', name: 'Xhosa' }
  ];

  const sortedLanguages = React.useMemo(() => {
    return [...languages].sort((a, b) => {
      if (a.name === languageName) return -1;
      if (b.name === languageName) return 1;
      return 0;
    });
  }, [languageName]);

  const handleLanguageChange = (e, lang) => {
    e.stopPropagation();
    changeLanguage(lang.code, lang.name);
    setShowLanguageSubmenu(false);
  };

  // Animation variants
  const sidebarVariants = {
    expanded: { width: 260 },
    collapsed: { width: 46 }
  };

  const itemVariants = {
    hidden: { opacity: 0, x: -10 },
    visible: { opacity: 1, x: 0 }
  };

  const dropdownVariants = {
    hidden: { opacity: 0, y: 10, scale: 0.95 },
    visible: { opacity: 1, y: 0, scale: 1 }
  };

  if (!isLoaded) {
    return (
      <div className="sidebar-loading">
        <div className="loading-spinner" />
      </div>
    );
  }

  return (
    <>
      <AnimatePresence>
        {isMobileOpen && (
          <motion.div
            className="mobile-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeMobileSidebar}
          />
        )}
      </AnimatePresence>

      <div
        className={`sidebar ${isMobileOpen ? 'mobile-open' : ''} ${isCollapsed ? 'collapsed' : ''}`}
      >
        {/* Header */}
        <div className="sidebar-header">
          <div className="sidebar-brand">
            <img src="/tuliza.jpg" alt="Tuliza" className="sidebar-tuliza-icon" />
            {!isCollapsed && <span className="sidebar-brand-name">Tuliza</span>}
          </div>
          <button
            className="toggle-btn"
            onClick={handleToggleBtnClick}
            title={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
            aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
              <path
                d={isCollapsed ? "M3 6h18M3 12h18M3 18h18" : "M15 18l-6-6 6-6"}
                stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>

        {/* New Chat Button */}
        <motion.button
          className="new-chat-btn"
          onClick={handleNewChat}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          title={t('newChat')}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
            <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          </svg>
          {!isCollapsed && <span>{t('newChat')}</span>}
        </motion.button>

        {/* Chat History — hidden when collapsed */}
        <motion.button
          className="voice-sidebar-btn"
          onClick={handleOpenVoice}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          title="Open Tuliza Voice"
        >
          <span className="voice-sidebar-glow" aria-hidden="true" />
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
            <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" stroke="currentColor" strokeWidth="2"/>
            <path d="M19 10v2a7 7 0 0 1-14 0v-2M12 19v4M8 23h8" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          </svg>
          {!isCollapsed && <span>Tuliza Voice</span>}
        </motion.button>

        {!isCollapsed && (
        <div className="chat-history">
          <h3 className="section-title">
            {pinnedChats.size > 0 ? 'Pinned & Recent' : 'Recent'}
          </h3>

          <div className="chat-list">
              {orderedChats.length === 0 && (
                <div className="sidebar-empty">
                  Your saved chats will appear here once you start a conversation.
                </div>
              )}

              {orderedChats.map((chat, index) => (
              <motion.div
                key={chat.url_code}
                className="chat-item-wrapper"
                initial="hidden"
                animate="visible"
                variants={itemVariants}
                transition={{ delay: index * 0.03 }}
                onMouseEnter={() => setHoveredChat(chat.url_code)}
                onMouseLeave={() => setHoveredChat(null)}
              >
                <Link
                  to={`/chat?chat=${chat.url_code}`}
                  className={`chat-item ${activeChat === chat.url_code ? 'active' : ''}`}                  onClick={() => {
                    setActiveChat(chat.url_code);
                    closeMobileSidebar();
                  }}
                  title={''}
                >
                  {pinnedChats.has(chat.url_code) && (
                    <div className="pin-indicator">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                        <path d="M16 4v4l-4 4-4-4V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2z" fill="currentColor"/>
                        <line x1="12" y1="14" x2="12" y2="20" stroke="currentColor" strokeWidth="2"/>
                      </svg>
                    </div>
                  )}
                  {!isCollapsed && (
                    <div className="chat-info">
                      {renamingChat === chat.url_code ? (
                        <form onSubmit={(e) => handleSaveRename(e, chat.url_code)} className="rename-form">
                          <input
                            type="text"
                            value={newChatTitle}
                            onChange={(e) => setNewChatTitle(e.target.value)}
                            onBlur={handleCancelRename}
                            onKeyDown={(e) => e.key === 'Escape' && handleCancelRename()}
                            className="rename-input"
                            autoFocus
                          />
                        </form>
                      ) : (
                        <>
                          <div className="chat-text-content">
                            <span className="chat-title">
                              {typingTitles[chat.url_code] !== undefined ? typingTitles[chat.url_code] : chat.title}
                              {typingTitles[chat.url_code] !== undefined && <span className="typing-cursor">|</span>}
                            </span>
                            <span className="chat-date">{formatDate(chat)}</span>
                          </div>
                        </>
                      )}
                    </div>
                  )}
                  {!isCollapsed && hoveredChat === chat.url_code && (
                    <div className="chat-dropdown-container">
                      <motion.button
                        className="menu-btn-modern"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          
                          // Calculate position for the popover
                          const rect = e.currentTarget.getBoundingClientRect();
                          setDropdownPosition({
                            top: rect.top,
                            left: 280
                          });
                          
                          setOpenDropdown(openDropdown === chat.url_code ? null : chat.url_code);
                        }}
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.8 }}
                        whileHover={{ scale: 1.1, backgroundColor: 'rgba(255,255,255,0.1)' }}
                        whileTap={{ scale: 0.9 }}
                        title="More options"
                      >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                          <circle cx="12" cy="5" r="2" fill="currentColor"/>
                          <circle cx="12" cy="12" r="2" fill="currentColor"/>
                          <circle cx="12" cy="19" r="2" fill="currentColor"/>
                        </svg>
                      </motion.button>
                      
                      {/* Dropdown is now rendered via portal at the end of component */}
                    </div>
                  )}
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
        )} {/* end !isCollapsed chat-history */}

        {/* Footer */}
        <div className="sidebar-footer">
          {/* Settings & Help - Opens overlay modal */}
          <button
            className="footer-link"
            onClick={() => setShowSettingsModal(true)}
            title="Settings"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2"/>
              <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            {!isCollapsed && <span>Settings</span>}
          </button>

          {/* Admin Link */}
          {isAdmin(clerkUser) && (
            <Link
              to="/admin"
              className={`footer-link ${location.pathname.startsWith('/admin') ? 'active' : ''}`}
              title="Admin"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                <path d="M12 2L2 7l10 5 10-5-10-5z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M2 17l10 5 10-5M2 12l10 5 10-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              {!isCollapsed && <span>Admin</span>}
            </Link>
          )}

          {/* User Profile */}
          <div className="user-profile-container">
            <motion.div 
              className="clerk-profile-section"
              whileHover={{ scale: 1.02 }}
            >
              {isCollapsed ? (
                <div className="user-profile-collapsed">
                  <UserButton 
                    appearance={clerkUserButtonDarkAppearance}
                    userProfileMode="modal"
                    afterSignOutUrl="/"
                    showName={false}
                  />
                </div>
              ) : (
                <div className="user-profile-full">
                  <div className="user-info">
                    <span className="user-name">{getUserName()}</span>
                    <span className="user-email">{getUserEmail()}</span>
                  </div>
                  <UserButton 
                    appearance={clerkUserButtonDarkAppearance}
                    userProfileMode="modal"
                    afterSignOutUrl="/"
                    showName={false}
                  />
                </div>
              )}
            </motion.div>
          </div>
        </div>
      </div>  {/* end .sidebar */}

      {/* Mobile Hamburger - hidden, handled by Chat topbar */}
      <motion.button
        className="mobile-hamburger"
        onClick={toggleMobileSidebar}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        aria-label="Toggle menu"
      >
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <path d="M3 12h18M3 6h18M3 18h18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
        </svg>
      </motion.button>

      {/* Settings Popover Menu - Gemini Style */}
      <AnimatePresence>
        {showSettingsModal && (
          <>
            <div
              className="settings-backdrop"
              onClick={() => {
                setShowSettingsModal(false);
                setActiveSubmenu(null);
              }}
            />
            <motion.div
              className={`settings-popover settings-popover-upward`}
              initial={{ opacity: 0, scale: 0.95, y: 10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 10 }}
              transition={{ duration: 0.15 }}
            >
              {/* Language Option with submenu */}
              <div
                className={`settings-menu-item ${activeSubmenu === 'language' ? 'active' : ''}`}
                onMouseEnter={() => setActiveSubmenu('language')}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                  <path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" stroke="currentColor" strokeWidth="2"/>
                </svg>
                <span>Language</span>
                <svg className="arrow-icon" width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path d="M9 18l6-6-6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>

                {/* Language Submenu */}
                <AnimatePresence>
                  {activeSubmenu === 'language' && (
                    <motion.div
                      className="settings-submenu language-submenu settings-submenu-upward"
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -10 }}
                      transition={{ duration: 0.12 }}
                    >
                      {languages.map(lang => (
                        <div
                          key={lang.code}
                          className={`submenu-item ${languageName === lang.name ? 'selected' : ''}`}
                          onClick={() => {
                            changeLanguage(lang.code, lang.name);
                            setShowSettingsModal(false);
                            setActiveSubmenu(null);
                          }}
                        >
                          <span>{lang.name}</span>
                          {languageName === lang.name && (
                            <svg className="check-icon" width="16" height="16" viewBox="0 0 24 24" fill="none">
                              <path d="M20 6L9 17l-5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          )}
                        </div>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              <div className="settings-divider" />

              {/* Help Option with submenu */}
              <div
                className={`settings-menu-item ${activeSubmenu === 'help' ? 'active' : ''}`}
                onMouseEnter={() => setActiveSubmenu('help')}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                  <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <circle cx="12" cy="17" r="1" fill="currentColor"/>
                </svg>
                <span>Help</span>
                <svg className="arrow-icon" width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path d="M9 18l6-6-6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>

                {/* Help Submenu */}
                <AnimatePresence>
                  {activeSubmenu === 'help' && (
                    <motion.div
                      className="settings-submenu settings-submenu-upward"
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -10 }}
                      transition={{ duration: 0.12 }}
                    >
                      <Link to="/help" className="submenu-item" onClick={() => { setShowSettingsModal(false); setActiveSubmenu(null); }}>
                        <span>Help Center</span>
                      </Link>
                      <Link to="/privacy" className="submenu-item" onClick={() => { setShowSettingsModal(false); setActiveSubmenu(null); }}>
                        <span>Privacy Policy</span>
                      </Link>
                      <Link to="/terms" className="submenu-item" onClick={() => { setShowSettingsModal(false); setActiveSubmenu(null); }}>
                        <span>Terms of Service</span>
                      </Link>
                      <a href="mailto:support@tuliza.org" className="submenu-item" onClick={() => { setShowSettingsModal(false); setActiveSubmenu(null); }}>
                        <span>Send Feedback</span>
                      </a>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Chat Dropdown Menu Portal - Rendered outside sidebar stacking context */}
      {ReactDOM.createPortal(
        <AnimatePresence>
          {openDropdown && (
            <>
              <div
                className="settings-backdrop"
                onClick={() => setOpenDropdown(null)}
              />
              <motion.div
                className="chat-dropdown-modern chat-dropdown-popover"
                style={{
                  top: `${dropdownPosition.top}px`,
                  left: `${dropdownPosition.left}px`
                }}
                initial={{ opacity: 0, scale: 0.95, y: -10 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: -10 }}
                transition={{ duration: 0.15 }}
              >
              <button 
                onClick={(e) => {
                  e.stopPropagation();
                  const chatCode = openDropdown;
                  handleShareChat(chatCode);
                }}
                className="dropdown-item-modern"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8M16 6l-4-4-4 4M12 2v13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span>Share</span>
              </button>
              <button 
                onClick={(e) => {
                  e.stopPropagation();
                  const chatCode = openDropdown;
                  handlePinChat(chatCode);
                }}
                className="dropdown-item-modern"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path d="M16 4v4l-4 4-4-4V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2z" fill={pinnedChats.has(openDropdown) ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2"/>
                  <line x1="12" y1="14" x2="12" y2="20" stroke="currentColor" strokeWidth="2"/>
                </svg>
                <span>{pinnedChats.has(openDropdown) ? 'Unpin' : 'Pin'}</span>
              </button>
              <button 
                onClick={(e) => {
                  e.stopPropagation();
                  const chatCode = openDropdown;
                  handleDeleteChat(e, chatCode);
                }}
                className="dropdown-item-modern delete-item"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span>Delete</span>
              </button>
            </motion.div>
          </>
          )}
        </AnimatePresence>,
        document.body
      )}

      <AnimatePresence>
        {sidebarNotice && (
          <motion.div
            className="sidebar-notice"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 12 }}
          >
            {sidebarNotice}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Sidebar;
